import React, { useState, useEffect } from 'react';
import { ContactCollection } from '../types/Collections';
import { Contact } from '../types/Contact';
import { supabase } from '../lib/supabase';
import { listCollectionsCore, listMembershipsForCollectionsCore, bulkAddMembersCore } from '@gv/core';
import { listContactsCore, type ContactCore } from '../lib/contactsCore';
import { User } from '@supabase/supabase-js';
import {
    X,
    Upload,
    Search,
    Plus,
    Check,
    AlertCircle,
    Users,
    FileText,
    UserPlus
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import NotificationToast from './NotificationToast';

interface BulkContactAdderProps {
    targetCollection?: ContactCollection | null;
    user: User;
    onClose: () => void;
    onContactsAdded: () => void;
}

interface MatchedContact {
    contact: Contact;
    matchType: 'exact' | 'partial' | 'fuzzy';
    confidence: number;
}

interface NameMatch {
    inputName: string;
    matches: MatchedContact[];
    selected?: Contact;
    needsConfirmation?: boolean;
    confirmationReason?: 'exact_duplicate' | 'fuzzy_match';
    confirmed?: boolean;
}

export default function BulkContactAdder({
    targetCollection,
    user,
    onClose,
    onContactsAdded
}: BulkContactAdderProps) {
    const [selectedCollection, setSelectedCollection] = useState<ContactCollection | null>(targetCollection);
    const [collections, setCollections] = useState<ContactCollection[]>([]);
    const [inputNames, setInputNames] = useState('');
    const [nameMatches, setNameMatches] = useState<NameMatch[]>([]);
    const [allContacts, setAllContacts] = useState<ContactCore[]>([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [step, setStep] = useState<'input' | 'review' | 'confirm' | 'complete'>('input');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [currentConfirmation, setCurrentConfirmation] = useState<NameMatch | null>(null);
    const { notification, showNotification, hideNotification } = useNotifications();

    useEffect(() => {
        fetchCollections();
        fetchAllContacts();
    }, []);

    const fetchCollections = async () => {
        try {
            const data = await listCollectionsCore(supabase as any, user.id);
            setCollections((data as any) || []);
        } catch (error) {
            console.error('Error fetching collections:', error);
        }
    };

    const fetchAllContacts = async () => {
        try {
            const data = await listContactsCore(supabase as any, { includeArchived: false, limit: 1000, offset: 0 });
            setAllContacts(data || []);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const normalizeString = (str: string): string => {
        return str.toLowerCase().trim().replace(/[^\w\s]/g, '');
    };

    const calculateNameSimilarity = (name1: string, name2: string): number => {
        const n1 = normalizeString(name1);
        const n2 = normalizeString(name2);

        if (n1 === n2) return 1;

        // Levenshtein distance for similarity
        const matrix = [];
        const len1 = n1.length;
        const len2 = n2.length;

        for (let i = 0; i <= len2; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= len1; j++) {
            matrix[0][j] = j;
        }

        for (let i = 1; i <= len2; i++) {
            for (let j = 1; j <= len1; j++) {
                if (n2.charAt(i - 1) === n1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }

        const distance = matrix[len2][len1];
        return 1 - distance / Math.max(len1, len2);
    };

    const findContactMatches = (inputName: string): MatchedContact[] => {
        const normalized = normalizeString(inputName);
        const matches: MatchedContact[] = [];

        // Parse input name
        const parts = normalized.split(/\s+/);
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ') || '';

        // Try different name formats
        const nameVariations = [
            `${firstName} ${lastName}`.trim(),
            `${lastName} ${firstName}`.trim(),
            firstName,
            lastName
        ].filter(Boolean);

        allContacts.forEach(contact => {
            const contactFullName = `${contact.first_name} ${contact.last_name}`;
            const contactNormalized = normalizeString(contactFullName);
            const contactFirstName = normalizeString(contact.first_name);
            const contactLastName = normalizeString(contact.last_name);

            for (const variation of nameVariations) {
                // Exact match
                if (contactNormalized === variation) {
                    matches.push({
                        contact,
                        matchType: 'exact',
                        confidence: 1.0
                    });
                    return;
                }

                // Partial match (starts with)
                if (contactNormalized.startsWith(variation) || variation.startsWith(contactNormalized)) {
                    matches.push({
                        contact,
                        matchType: 'partial',
                        confidence: 0.8
                    });
                    return;
                }

                // Check individual name parts
                if (firstName && (contactFirstName.includes(firstName) || firstName.includes(contactFirstName))) {
                    if (!lastName || contactLastName.includes(lastName) || lastName.includes(contactLastName)) {
                        matches.push({
                            contact,
                            matchType: 'partial',
                            confidence: 0.7
                        });
                        return;
                    }
                }
            }

            // Fuzzy match as fallback
            const similarity = calculateNameSimilarity(contactNormalized, normalized);
            if (similarity > 0.6) {
                matches.push({
                    contact,
                    matchType: 'fuzzy',
                    confidence: similarity
                });
            }
        });

        // Remove duplicates and sort by confidence
        const uniqueMatches = matches.filter((match, index, self) =>
            index === self.findIndex(m => m.contact.id === match.contact.id)
        );

        return uniqueMatches
            .sort((a, b) => b.confidence - a.confidence)
            .slice(0, 5); // Limit to top 5 matches
    };

    const processNames = () => {
        if (!inputNames.trim()) return;

        setLoading(true);
        const names = inputNames
            .split('\n')
            .map(name => name.trim())
            .filter(name => name.length > 0);

        const matches: NameMatch[] = names.map(name => {
            const contactMatches = findContactMatches(name);

            // Determine if this needs confirmation
            let needsConfirmation = false;
            let confirmationReason: 'exact_duplicate' | 'fuzzy_match' | undefined;

            // Check for exact matches (potential duplicates)
            const exactMatches = contactMatches.filter(m => m.matchType === 'exact');
            if (exactMatches.length > 0) {
                needsConfirmation = true;
                confirmationReason = 'exact_duplicate';
            }
            // Check for fuzzy matches that need confirmation (not exact but close)
            else if (contactMatches.length > 0 && contactMatches[0].matchType === 'fuzzy' && contactMatches[0].confidence > 0.7) {
                needsConfirmation = true;
                confirmationReason = 'fuzzy_match';
            }

            return {
                inputName: name,
                matches: contactMatches,
                needsConfirmation,
                confirmationReason,
                confirmed: !needsConfirmation // Auto-confirm if no confirmation needed
            };
        });

        setNameMatches(matches);

        // Check if any matches need confirmation
        const needsConfirmation = matches.some(m => m.needsConfirmation && !m.confirmed);
        if (needsConfirmation) {
            setStep('confirm');
            // Set the first unconfirmed match for review
            const firstUnconfirmed = matches.find(m => m.needsConfirmation && !m.confirmed);
            setCurrentConfirmation(firstUnconfirmed || null);
            setShowConfirmModal(true);
        } else {
            setStep('review');
        }

        setLoading(false);
    };

    const handleContactSelect = (nameMatchIndex: number, contact: ContactCore) => {
        setNameMatches(prev => prev.map((match, index) =>
            index === nameMatchIndex ? { ...match, selected: contact } : match
        ));
    };

    const handleConfirmationResponse = (accept: boolean) => {
        if (!currentConfirmation) return;

        const updatedMatches = nameMatches.map(match => {
            if (match.inputName === currentConfirmation.inputName) {
                return {
                    ...match,
                    confirmed: true,
                    selected: accept && match.matches.length > 0 ? match.matches[0].contact : undefined
                };
            }
            return match;
        });

        setNameMatches(updatedMatches);

        // Find next unconfirmed match
        const nextUnconfirmed = updatedMatches.find(m =>
            m.needsConfirmation &&
            !m.confirmed &&
            m.inputName !== currentConfirmation.inputName
        );

        if (nextUnconfirmed) {
            setCurrentConfirmation(nextUnconfirmed);
        } else {
            // All confirmations complete, move to review
            setShowConfirmModal(false);
            setCurrentConfirmation(null);
            setStep('review');
        }
    };

    const skipConfirmation = () => {
        if (!currentConfirmation) return;

        const updatedMatches = nameMatches.map(match => {
            if (match.inputName === currentConfirmation.inputName) {
                return {
                    ...match,
                    confirmed: true,
                    selected: undefined // Skip this contact
                };
            }
            return match;
        });

        setNameMatches(updatedMatches);

        // Find next unconfirmed match
        const nextUnconfirmed = updatedMatches.find(m =>
            m.needsConfirmation &&
            !m.confirmed &&
            m.inputName !== currentConfirmation.inputName
        );

        if (nextUnconfirmed) {
            setCurrentConfirmation(nextUnconfirmed);
        } else {
            // All confirmations complete, move to review
            setShowConfirmModal(false);
            setCurrentConfirmation(null);
            setStep('review');
        }
    };

    const addContactsToCollection = async () => {
        if (!selectedCollection) {
            showNotification('Please select a collection', 'error');
            return;
        }

        const selectedContacts = nameMatches
            .filter(match => match.selected)
            .map(match => match.selected!);

        if (selectedContacts.length === 0) {
            showNotification('Please select at least one contact to add', 'error');
            return;
        }

        setProcessing(true);
        try {
            // Check which contacts are already in the collection
            const existingRows = await listMembershipsForCollectionsCore(
                supabase as any,
                [selectedCollection.id],
                selectedContacts.map(c => c.id)
            );
            const existingContactIds = new Set((existingRows || []).map((m: any) => m.contact_id));
            const newContacts = selectedContacts.filter(c => !existingContactIds.has(c.id));

            if (newContacts.length === 0) {
                showNotification('All selected contacts are already in this collection', 'error');
                setProcessing(false);
                return;
            }

            // Add new contacts to collection via core
            await bulkAddMembersCore(
                supabase as any,
                newContacts.map(contact => ({
                    collection_id: selectedCollection.id,
                    contact_id: contact.id,
                    added_by: user.id,
                }))
            );

            setStep('complete');
            showNotification(`Added ${newContacts.length} contact${newContacts.length !== 1 ? 's' : ''} to "${selectedCollection.name}"`, 'success');
            onContactsAdded();
        } catch (error) {
            console.error('Error adding contacts to collection:', error);
            showNotification('Failed to add contacts to collection', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const getMatchTypeColor = (matchType: string) => {
        switch (matchType) {
            case 'exact': return 'text-green-400';
            case 'partial': return 'text-yellow-400';
            case 'fuzzy': return 'text-blue-400';
            default: return 'text-gray-400';
        }
    };

    const getMatchTypeLabel = (matchType: string) => {
        switch (matchType) {
            case 'exact': return 'Exact Match';
            case 'partial': return 'Partial Match';
            case 'fuzzy': return 'Similar Name';
            default: return 'Unknown';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-black border border-gray-800 w-full max-w-4xl max-h-[90vh] flex flex-col">
                {notification && (
                    <NotificationToast notification={notification} onClose={hideNotification} />
                )}
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-800">
                    <div>
                        <h2 className="text-xl font-light text-white tracking-wide">
                            Bulk Contact Import
                        </h2>
                        <p className="text-gray-400 text-sm">
                            {step === 'input' && 'Paste contact names to find and add them to a collection'}
                            {step === 'confirm' && 'Confirm potential matches and duplicates'}
                            {step === 'review' && 'Review matches and select contacts to add'}
                            {step === 'complete' && 'Contacts successfully added to collection'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Step 1: Input */}
                {step === 'input' && (
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Collection Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Target Collection *
                            </label>
                            <select
                                value={selectedCollection?.id || ''}
                                onChange={(e) => {
                                    const collection = collections.find(c => c.id === e.target.value);
                                    setSelectedCollection(collection || null);
                                }}
                                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-gray-600"
                                required
                            >
                                <option value="">Select a collection</option>
                                {collections.map(collection => (
                                    <option key={collection.id} value={collection.id}>
                                        {collection.name}
                                    </option>
                                ))}
                            </select>
                            {collections.length === 0 && (
                                <p className="text-gray-400 text-sm mt-2">
                                    No collections found. Create a collection first.
                                </p>
                            )}
                        </div>

                        {/* Name Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Contact Names
                            </label>
                            <textarea
                                value={inputNames}
                                onChange={(e) => setInputNames(e.target.value)}
                                placeholder="Paste contact names here, one per line:&#10;&#10;John Smith&#10;Jane Doe&#10;Robert Johnson&#10;..."
                                rows={12}
                                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-600 resize-none font-mono text-sm"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                Enter one name per line. Supports formats like "First Last" or "Last, First"
                            </p>
                        </div>
                    </div>
                )}

                {/* Step 2: Review Matches */}
                {step === 'review' && (
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-6 space-y-4">
                            {nameMatches.map((nameMatch, index) => (
                                <div key={index} className="border border-gray-700 rounded-lg">
                                    <div className="p-4 bg-gray-900 border-b border-gray-700">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-medium text-white">"{nameMatch.inputName}"</h3>
                                            <div className="flex items-center space-x-2">
                                                {nameMatch.confirmed && nameMatch.selected && (
                                                    <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">
                                                        CONFIRMED
                                                    </span>
                                                )}
                                                {nameMatch.confirmed && !nameMatch.selected && (
                                                    <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded">
                                                        SKIPPED
                                                    </span>
                                                )}
                                                <span className="text-sm text-gray-400">
                                                    {nameMatch.matches.length} match{nameMatch.matches.length !== 1 ? 'es' : ''} found
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        {nameMatch.matches.length > 0 ? (
                                            <div className="space-y-3">
                                                {nameMatch.matches.map((match, matchIndex) => (
                                                    <div
                                                        key={matchIndex}
                                                        className={`flex items-center justify-between p-3 border rounded transition-colors cursor-pointer ${nameMatch.selected?.id === match.contact.id
                                                            ? 'border-blue-500 bg-blue-500/10'
                                                            : 'border-gray-700 hover:border-gray-600'
                                                            }`}
                                                        onClick={() => handleContactSelect(index, match.contact)}
                                                    >
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
                                                                {match.contact.profile_image_url ? (
                                                                    <img
                                                                        src={match.contact.profile_image_url}
                                                                        alt={`${match.contact.first_name} ${match.contact.last_name}`}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-300 font-medium text-sm">
                                                                        {match.contact.first_name?.[0]}{match.contact.last_name?.[0]}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div>
                                                                <p className="text-white font-medium">
                                                                    {match.contact.first_name} {match.contact.last_name}
                                                                </p>
                                                                {match.contact.company && (
                                                                    <p className="text-gray-400 text-sm">{match.contact.company}</p>
                                                                )}
                                                                <div className="flex items-center space-x-2 mt-1">
                                                                    <span className={`text-xs ${getMatchTypeColor(match.matchType)}`}>
                                                                        {getMatchTypeLabel(match.matchType)}
                                                                    </span>
                                                                    <span className="text-gray-500 text-xs">
                                                                        {Math.round(match.confidence * 100)}% confidence
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {nameMatch.selected?.id === match.contact.id && (
                                                            <Check className="w-5 h-5 text-blue-400" />
                                                        )}
                                                    </div>
                                                ))}

                                                {/* Option to deselect if auto-confirmed */}
                                                {nameMatch.confirmed && nameMatch.selected && (
                                                    <div className="pt-2 border-t border-gray-700">
                                                        <button
                                                            onClick={() => setNameMatches(prev => prev.map((match, idx) =>
                                                                idx === index ? { ...match, selected: undefined } : match
                                                            ))}
                                                            className="text-sm text-gray-400 hover:text-white transition-colors"
                                                        >
                                                            Deselect this contact
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 text-gray-400">
                                                <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                                                <p className="text-sm">No matches found for this name</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 3: Complete */}
                {step === 'complete' && (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-light text-white mb-2">Contacts Added Successfully</h3>
                            <p className="text-gray-400 mb-6">
                                {nameMatches.filter(m => m.selected).length} contacts have been added to "{selectedCollection?.name}"
                            </p>
                            <button
                                onClick={onClose}
                                className="px-6 py-3 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}

                {/* Confirmation Modal */}
                {showConfirmModal && currentConfirmation && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                        <div className="bg-black border border-gray-800 max-w-2xl w-full rounded-lg">
                            <div className="p-6 border-b border-gray-800">
                                <div className="flex items-center space-x-3 mb-4">
                                    <AlertCircle className="w-6 h-6 text-yellow-400" />
                                    <h3 className="text-lg font-medium text-white">
                                        {currentConfirmation.confirmationReason === 'exact_duplicate'
                                            ? 'Potential Duplicate Detected'
                                            : 'Similar Contact Found'
                                        }
                                    </h3>
                                </div>
                                <p className="text-gray-400 text-sm">
                                    {currentConfirmation.confirmationReason === 'exact_duplicate'
                                        ? `Found an existing contact with the exact name "${currentConfirmation.inputName}". This might be a duplicate.`
                                        : `Found a contact with a similar name to "${currentConfirmation.inputName}". Please confirm if this is the same person.`
                                    }
                                </p>
                            </div>

                            <div className="p-6">
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-white font-medium mb-2">Your Input:</h4>
                                        <div className="bg-gray-900 border border-gray-700 p-3 rounded">
                                            <span className="text-white">{currentConfirmation.inputName}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="text-white font-medium mb-2">Found Contact:</h4>
                                        {currentConfirmation.matches.length > 0 && (
                                            <div className="bg-gray-900 border border-gray-700 p-3 rounded">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
                                                        {currentConfirmation.matches[0].contact.profile_image_url ? (
                                                            <img
                                                                src={currentConfirmation.matches[0].contact.profile_image_url}
                                                                alt={`${currentConfirmation.matches[0].contact.first_name} ${currentConfirmation.matches[0].contact.last_name}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <span className="text-gray-300 font-medium">
                                                                {currentConfirmation.matches[0].contact.first_name?.[0]}
                                                                {currentConfirmation.matches[0].contact.last_name?.[0]}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-white font-medium">
                                                            {currentConfirmation.matches[0].contact.first_name} {currentConfirmation.matches[0].contact.last_name}
                                                        </p>
                                                        {currentConfirmation.matches[0].contact.company && (
                                                            <p className="text-gray-400 text-sm">{currentConfirmation.matches[0].contact.company}</p>
                                                        )}
                                                        {currentConfirmation.matches[0].contact.email && (
                                                            <p className="text-gray-400 text-sm">{currentConfirmation.matches[0].contact.email}</p>
                                                        )}
                                                        <div className="flex items-center space-x-2 mt-1">
                                                            <span className={`text-xs ${getMatchTypeColor(currentConfirmation.matches[0].matchType)}`}>
                                                                {getMatchTypeLabel(currentConfirmation.matches[0].matchType)}
                                                            </span>
                                                            <span className="text-gray-500 text-xs">
                                                                {Math.round(currentConfirmation.matches[0].confidence * 100)}% confidence
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 border-t border-gray-800 flex justify-end space-x-4">
                                <button
                                    onClick={skipConfirmation}
                                    className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    Skip This Contact
                                </button>
                                <button
                                    onClick={() => handleConfirmationResponse(false)}
                                    className="px-6 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors rounded"
                                >
                                    No, Different Person
                                </button>
                                <button
                                    onClick={() => handleConfirmationResponse(true)}
                                    className="px-6 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors rounded"
                                >
                                    {currentConfirmation.confirmationReason === 'exact_duplicate'
                                        ? 'Yes, Same Person (Add to Collection)'
                                        : 'Yes, Same Person'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-800">
                    {step === 'input' && (
                        <>
                            <div className="text-sm text-gray-400">
                                {inputNames.split('\n').filter(n => n.trim()).length} names entered
                            </div>
                            <div className="flex space-x-4">
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={processNames}
                                    disabled={!inputNames.trim() || !selectedCollection || loading}
                                    className="inline-flex items-center px-6 py-2 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <Search className="w-4 h-4 mr-2" />
                                            Find Matches
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}

                    {step === 'confirm' && (
                        <>
                            <div className="text-sm text-gray-400">
                                {nameMatches.filter(m => m.confirmed).length} of {nameMatches.filter(m => m.needsConfirmation).length} confirmations complete
                            </div>
                            <div className="flex space-x-4">
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        setCurrentConfirmation(null);
                                        setStep('input');
                                    }}
                                    className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    Back to Input
                                </button>
                                <span className="text-sm text-gray-400">
                                    Review each potential match
                                </span>
                            </div>
                        </>
                    )}

                    {step === 'review' && (
                        <>
                            <div className="text-sm text-gray-400">
                                {nameMatches.filter(m => m.selected).length} of {nameMatches.length} names selected
                            </div>
                            <div className="flex space-x-4">
                                <button
                                    onClick={() => setStep('input')}
                                    className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={addContactsToCollection}
                                    disabled={!nameMatches.some(m => m.selected) || processing}
                                    className="inline-flex items-center px-6 py-2 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {processing ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="w-4 h-4 mr-2" />
                                            Add to Collection
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
